/**
 * SensitiveChangeModal — v7.3.347 SECURITY
 *
 * Three-field re-authentication + 6-digit OTP gate for sensitive profile
 * changes (email, username, password). Backed by:
 *   - POST /api/sensitive-change/initiate  (verifies current pw+email+username, dispatches OTP)
 *   - POST /api/sensitive-change/verify    (verifies OTP, applies the change atomically)
 *
 * The parent component is responsible for collecting and validating the
 * NEW value (e.g. UsernameEditor collects new username, SecurePasswordChangeForm
 * collects new password). This modal handles ONLY the re-auth + OTP exchange.
 *
 * On success, parent receives onSuccess(); the modal renders a brief success
 * state then closes.
 */

import React, { useEffect, useRef, useState } from 'react';
import { getSessionHeaders } from '../../lib/serverSession';

export type SensitiveChangeType = 'email' | 'username' | 'password';

interface SensitiveChangeModalProps {
  changeType: SensitiveChangeType;
  newValue: string;
  currentEmail: string;
  currentUsername: string;
  onSuccess: (result: { changeType: SensitiveChangeType; changedTo?: string; sessionsInvalidated?: boolean }) => void;
  onClose: () => void;
}

const TITLES: Record<SensitiveChangeType, string> = {
  email: 'Confirm Email Change',
  username: 'Confirm Username Change',
  password: 'Confirm Password Change'
};

const ACTION_LABELS: Record<SensitiveChangeType, string> = {
  email: 'change your email address',
  username: 'change your username',
  password: 'change your password'
};

export const SensitiveChangeModal: React.FC<SensitiveChangeModalProps> = ({
  changeType,
  newValue,
  currentEmail,
  currentUsername,
  onSuccess,
  onClose
}) => {
  const [step, setStep] = useState<'reauth' | 'otp' | 'success'>('reauth');
  const [pwd, setPwd] = useState('');
  const [emailField, setEmailField] = useState('');
  const [usernameField, setUsernameField] = useState('');
  const [otp, setOtp] = useState('');
  const [changeRequestId, setChangeRequestId] = useState<string | null>(null);
  const [maskedEmail, setMaskedEmail] = useState<string>('');
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultSummary, setResultSummary] = useState<string>('');
  const otpInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (step === 'otp' && otpInputRef.current) otpInputRef.current.focus();
  }, [step]);

  const submitReauth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!pwd || !emailField || !usernameField) {
      setError('All three fields are required.');
      return;
    }
    setLoading(true);
    try {
      const r = await fetch('/api/sensitive-change/initiate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          changeType,
          currentPassword: pwd,
          currentEmail: emailField,
          currentUsername: usernameField,
          newValue
        })
      });
      const data = await r.json();
      if (!r.ok || !data.success) {
        setError(data.error || 'Re-authentication failed.');
        return;
      }
      setChangeRequestId(data.changeRequestId);
      setMaskedEmail(data.maskedEmail || '');
      setStep('otp');
      setPwd('');
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!changeRequestId) {
      setError('Verification session expired. Please start over.');
      return;
    }
    if (!/^\d{6}$/.test(otp.trim())) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setLoading(true);
    try {
      const r = await fetch('/api/sensitive-change/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ changeRequestId, otp: otp.trim() })
      });
      const data = await r.json();
      if (!r.ok || !data.success) {
        if (typeof data.attemptsRemaining === 'number') {
          setAttemptsRemaining(data.attemptsRemaining);
        }
        setError(data.error || 'Verification failed.');
        return;
      }
      setResultSummary(data.message || `${changeType} updated.`);
      setStep('success');
      onSuccess({
        changeType,
        changedTo: data.changedTo,
        sessionsInvalidated: data.sessionsInvalidated
      });
      if (changeType !== 'password') {
        setTimeout(() => onClose(), 2500);
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="max-w-md w-full bg-gray-800 rounded-2xl border border-gray-700 p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">{TITLES[changeType]}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white" aria-label="Close">✕</button>
        </div>

        {error && (
          <div className="bg-red-900/40 border border-red-500/40 text-red-300 text-sm rounded-lg px-3 py-2 mb-3">
            {error}
            {attemptsRemaining !== null && step === 'otp' && (
              <div className="text-xs text-red-200/80 mt-1">Attempts remaining: {attemptsRemaining}</div>
            )}
          </div>
        )}

        {step === 'reauth' && (
          <form onSubmit={submitReauth} className="space-y-3">
            <p className="text-sm text-gray-300">
              For your security, confirm your <strong>current</strong> password, email, and username before we {ACTION_LABELS[changeType]}.
            </p>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Current Password</label>
              <input
                type="password"
                autoComplete="current-password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Current Email</label>
              <input
                type="email"
                autoComplete="email"
                value={emailField}
                onChange={(e) => setEmailField(e.target.value)}
                placeholder={currentEmail ? `e.g. ${currentEmail.replace(/^(.).*(@.*)$/, '$1***$2')}` : ''}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Current Username</label>
              <input
                type="text"
                autoComplete="username"
                value={usernameField}
                onChange={(e) => setUsernameField(e.target.value)}
                placeholder={currentUsername || ''}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                required
              />
            </div>
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-yellow-600 hover:bg-yellow-500 disabled:opacity-50 text-white rounded-lg font-medium"
              >
                {loading ? 'Verifying…' : 'Send Code'}
              </button>
            </div>
          </form>
        )}

        {step === 'otp' && (
          <form onSubmit={submitOtp} className="space-y-3">
            <p className="text-sm text-gray-300">
              Enter the 6-digit verification code we just sent to <strong>{maskedEmail || 'your registered email'}</strong>. The code expires in 15 minutes.
            </p>
            <input
              ref={otpInputRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-3 text-center text-white text-2xl tracking-[0.5em] font-mono focus:outline-none focus:ring-2 focus:ring-yellow-500"
              placeholder="------"
              required
            />
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => { setStep('reauth'); setError(null); setOtp(''); }}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
                disabled={loading}
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="flex-1 px-4 py-2 bg-yellow-600 hover:bg-yellow-500 disabled:opacity-50 text-white rounded-lg font-medium"
              >
                {loading ? 'Verifying…' : 'Confirm Change'}
              </button>
            </div>
          </form>
        )}

        {step === 'success' && (
          <div className="text-center py-4">
            <div className="text-green-400 text-4xl mb-3">✓</div>
            <h4 className="text-lg font-semibold text-white mb-2">Change Confirmed</h4>
            <p className="text-gray-300 text-sm">{resultSummary}</p>
            {changeType === 'password' && (
              <button
                onClick={onClose}
                className="mt-4 px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg"
              >
                Close
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SensitiveChangeModal;
